import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { TrendingUp, Music, Sparkles, BarChart3, Activity, Zap, RefreshCw } from "lucide-react";
import { Card, CardContent } from "../../ui/card";
import { useTranslation } from "react-i18next";

const DEFAULT_COLORS = [
  "#f59e0b", // Amber-500 (Orange)
  "#000000", // Black
  "#ea580c", // Orange-600 (Deeper Orange)
];

const DEFAULT_GRADIENT_COLORS = [
  { from: "#f59e0b", to: "#facc15" }, // Amber-500 to Yellow-500 (Orange Gradient)
  { from: "#000000", to: "#282c34" }, // Black to Dark Gray
  { from: "#ea580c", to: "#f59e0b" }, // Orange-600 to Amber-500
];

const ICONS = [
  <Music className="w-8 h-8 text-white" />,
  <TrendingUp className="w-8 h-8 text-white" />,
  <Sparkles className="w-8 h-8 text-white" />,
  <BarChart3 className="w-8 h-8 text-white" />,
  <Activity className="w-8 h-8 text-white" />,
  <Zap className="w-8 h-8 text-white" />,
];

const getResponsiveChartHeight = () => {
  if (typeof window === "undefined") return 200;
  if (window.innerWidth < 400) return Math.max(window.innerWidth * 0.7, 100);
  if (window.innerWidth < 640) return Math.max(window.innerWidth * 0.5, 120);
  if (window.innerWidth < 1280) return 180;
  return 230;
};

// Tooltip component
const Tooltip = ({ visible, x, y, content }) => (
  <div
    className={`pointer-events-none fixed z-50 transition-opacity duration-150 ${visible ? 'opacity-100' : 'opacity-0'}`}
    style={{ left: x, top: y, minWidth: 80 }}
    role="tooltip"
  >
    <div className="bg-white border border-slate-300 rounded-lg shadow-lg px-3 py-2 text-xs text-slate-800">
      {content}
    </div>
  </div>
);

// DonutChart subcomponent
const DonutChart = ({ segments, size, radius, strokeWidth, circumference, animationDuration, isAnimated, onSegmentHover, onSegmentFocus, onSegmentBlur, focusedIndex, ariaLabel }) => (
  <svg
    width={size}
    height={size}
    className="relative z-10 filter"
    style={{ height: size, width: size }}
    role="img"
    aria-label={ariaLabel}
    tabIndex={0}
  >
    <defs>
      {segments.map((seg, i) => (
        <linearGradient key={i} id={`gradient-${i}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={seg.gradient.from} />
          <stop offset="100%" stopColor={seg.gradient.to} />
        </linearGradient>
      ))}

    </defs>
    {segments.map((seg, i) => {
      const segmentLength = seg.end - seg.start;
      const dash = circumference * segmentLength;
      const gap = circumference * (1 - segmentLength);
      const animatedDash = isAnimated ? dash : 0;
      const actualStrokeWidth = strokeWidth;
      const actualRadius = radius;
      return (
        <circle
          key={i}
          cx={size / 2}
          cy={size / 2}
          r={actualRadius}
          fill="none"
          stroke={`url(#gradient-${i})`}
          strokeWidth={actualStrokeWidth}
          strokeDasharray={`${animatedDash} ${gap}`}
          strokeDashoffset={-circumference * seg.start}
          strokeLinecap="round"
          filter={"url(#shadow)"}
          style={{
            transition: `all ${animationDuration * 0.8}ms cubic-bezier(0.4, 0, 0.2, 1)` + (focusedIndex === i ? ', filter 0.2s' : ''),
            cursor: "pointer",
            opacity: 1,
            outline: focusedIndex === i ? '2px solid #2563eb' : 'none',
          }}
          tabIndex={0}
          aria-label={`${seg.name}: ${seg.value} (${Math.round(seg.percent)}%)`}
          onMouseEnter={e => onSegmentHover(i, e)}
          onMouseMove={e => onSegmentHover(i, e)}
          onMouseLeave={onSegmentBlur}
          onFocus={e => onSegmentFocus(i, e)}
          onBlur={onSegmentBlur}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              onSegmentHover(i, e);
            }
          }}
        />
      );
    })}
  </svg>
);

// Legend subcomponent
const Legend = ({ segments, showPercentages }) => (
  <div className="flex flex-wrap items-center justify-center gap-3 mt-6 max-w-full">
    {segments.map((seg, i) => (
      <div
        key={i}
        className="flex items-center gap-2 px-3 py-2 rounded-full cursor-default bg-white/40"
        tabIndex={0}
        aria-label={`${seg.name}: ${seg.value} (${Math.round(seg.percent)}%)`}
      >
        <div className="relative">
          <div
            className="w-4 h-4 rounded-full shadow-md"
            style={{
              background: `linear-gradient(135deg, ${seg.gradient.from}, ${seg.gradient.to})`,
            }}
          />
        </div>
        <span className="text-sm font-semibold text-slate-700 whitespace-nowrap">
          {seg.name}
        </span>
        <span className="text-xs text-slate-500 font-medium bg-slate-100 px-2 py-1 rounded-full">
          {seg.value}
        </span>
        {showPercentages && (
          <span className="text-xs text-slate-400 font-medium">
            {Math.round(seg.percent)}%
          </span>
        )}
      </div>
    ))}
  </div>
);

const CircleChart = ({
  data: propData = [
    { name: "January", value: 24 },
    { name: "February", value: 18 },
    { name: "March", value: 32 }
  ],
  title,
  icon = <Music className="w-8 h-8 text-white" />, // not used in UI, but kept for API compatibility
  showPercentages = true,
  animationDuration = 1000,
  strokeWidth: customStrokeWidth,
  minSegmentAngle = 0.05,
  dynamic = true,
  colors = DEFAULT_COLORS,
  gradients = DEFAULT_GRADIENT_COLORS,
  ariaLabel,
}) => {
  const { t } = useTranslation();
  // Responsive chart height
  const [chartHeight, setChartHeight] = useState(getResponsiveChartHeight());
  useEffect(() => {
    const handleResize = () => setChartHeight(getResponsiveChartHeight());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const size = chartHeight;
  const strokeWidth = customStrokeWidth || size * 0.12;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  // Dynamic data state
  const [data, setData] = useState(propData);
  const [loading, setLoading] = useState(dynamic);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!dynamic) return;
    setLoading(true);
    setError(null);
    fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000'}/api/sync/songs-added-this-month`)
      .then(res => {
        if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`);
        return res.json();
      })
      .then(result => {
        if (!result || !Array.isArray(result.data)) throw new Error("Invalid data");
        // Get last 3 months
        const now = new Date();
        const months = [];
        for (let i = 2; i >= 0; i--) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          months.push({
            name: d.toLocaleString('default', { month: 'long' }),
            month: d.getMonth() + 1,
          });
        }
        const chartData = months.map(m => {
          const found = result.data.find(item => item.month === m.month);
          return {
            name: t(`months.${m.name}`, m.name),
            value: found ? found.totalSongsAdded || 0 : 0,
          };
        });
        setData(chartData);
        setLoading(false);
      })
      .catch(e => {
        setError(e.message);
        setData(propData);
        setLoading(false);
      });
  }, [dynamic, t]);

  // Memoized calculations
  const { total, segments, hasData } = useMemo(() => {
    const dataTotal = data.reduce((sum, item) => sum + item.value, 0);
    let accumulator = 0;
    const processedSegments = data.map((item, index) => {
      const value = item.value;
      const percent = dataTotal > 0 ? value / dataTotal : 0;
      const adjustedPercent = dataTotal > 0 ? Math.max(percent, minSegmentAngle) : 0;
      const start = accumulator;
      const end = accumulator + adjustedPercent;
      accumulator = end;
      return {
        ...item,
        color: colors[index % colors.length],
        gradient: gradients[index % gradients.length],
        percent: dataTotal > 0 ? percent * 100 : 0,
        start,
        end,
        originalIndex: index,
      };
    });
    return {
      total: dataTotal,
      segments: processedSegments,
      hasData: data && data.length > 0
    };
  }, [data, minSegmentAngle, colors, gradients]);

  // Animation and interaction state
  const [isAnimated, setIsAnimated] = useState(false);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: "" });
  const [focusedIndex, setFocusedIndex] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setIsAnimated(true), 300);
    return () => clearTimeout(timer);
  }, []);

  // Tooltip handlers
  const handleSegmentHover = (i, e) => {
    const seg = segments[i];
    let x = 0, y = 0;
    if (e.touches && e.touches.length > 0) {
      x = e.touches[0].clientX;
      y = e.touches[0].clientY;
    } else if (e.clientX && e.clientY) {
      x = e.clientX;
      y = e.clientY;
    }
    setTooltip({
      visible: true,
      x: x + 12,
      y: y - 12,
      content: `${seg.name}: ${seg.value} (${Math.round(seg.percent)}%)`,
    });
    setFocusedIndex(i);
  };
  const handleSegmentBlur = () => {
    setTooltip({ ...tooltip, visible: false });
    setFocusedIndex(null);
  };
  const handleSegmentFocus = (i, e) => {
    handleSegmentHover(i, e);
    setFocusedIndex(i);
  };

  if (loading) {
    return (
      <Card className="rounded-2xl border border-slate-200 shadow-lg bg-white overflow-hidden w-full max-w-none mx-auto h-full">
        <CardContent className="p-2 sm:p-4 lg:p-6 h-full flex flex-col items-center justify-center">
          <RefreshCw className="w-8 h-8 text-slate-400 animate-spin mb-4" />
          <h3 className="text-xs sm:text-sm lg:text-lg font-semibold text-slate-800 mb-4">{title || t('circleChart.title')}</h3>
          <p className="text-slate-500 text-sm">{t('circleChart.loading')}</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="rounded-2xl border border-slate-200 shadow-lg bg-white overflow-hidden w-full max-w-none mx-auto h-full">
        <CardContent className="p-2 sm:p-4 lg:p-6 h-full flex flex-col items-center justify-center">
          <h3 className="text-xs sm:text-sm lg:text-lg font-semibold text-slate-800 mb-4">{title || t('circleChart.title')}</h3>
          <div className="flex flex-col items-center justify-center space-y-4 opacity-60">
            <div className="w-32 h-32 rounded-full border-4 border-dashed border-slate-300 flex items-center justify-center">
              <Music className="w-8 h-8 text-slate-400" />
            </div>
            <p className="text-red-500 text-sm">{t('circleChart.error')}: {error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!hasData) {
    return (
      <Card className="rounded-2xl border border-slate-200 shadow-lg bg-white overflow-hidden w-full max-w-none mx-auto h-full">
        <CardContent className="p-2 sm:p-4 lg:p-6 h-full flex flex-col items-center justify-center">
          <h3 className="text-xs sm:text-sm lg:text-lg font-semibold text-slate-800 mb-4">
            {title || t('circleChart.title')}
          </h3>
          <div className="flex flex-col items-center justify-center space-y-4 opacity-60">
            <div className="w-32 h-32 rounded-full border-4 border-dashed border-slate-300 flex items-center justify-center">
              <Music className="w-8 h-8 text-slate-400" />
            </div>
            <p className="text-slate-500 text-sm">{t('circleChart.noData')}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded-2xl border border-slate-200 shadow-lg bg-white overflow-hidden w-full max-w-none mx-auto">
      <CardContent className="p-2 sm:p-4 lg:p-6 flex flex-col items-center justify-start">
        {/* Title */}
        <h3 className="text-xs sm:text-sm lg:text-lg font-semibold text-slate-800 mb-4">
          {title || t('circleChart.title')}
        </h3>
        <div className="relative flex items-center justify-center w-full" style={{ height: chartHeight, minHeight: 120 }}>
          {/* Background grid */}
          <svg width={size} height={size} className="absolute left-0 top-0 pointer-events-none opacity-20" style={{ height: chartHeight, width: chartHeight }}>
            {[0.3, 0.6, 0.9].map((frac, i) => (
              <circle
                key={i}
                cx={size / 2}
                cy={size / 2}
                r={radius * frac}
                fill="none"
                stroke="url(#gridGradient)"
                strokeWidth={1}
                strokeDasharray="4,4"
              />
            ))}
            <defs>
              <linearGradient id="gridGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#e2e8f0" />
                <stop offset="100%" stopColor="#cbd5e1" />
              </linearGradient>
            </defs>
          </svg>
          {/* Main donut chart with interactivity */}
          <DonutChart
            segments={segments}
            size={size}
            radius={radius}
            strokeWidth={strokeWidth}
            circumference={circumference}
            animationDuration={animationDuration}
            isAnimated={isAnimated}
            onSegmentHover={handleSegmentHover}
            onSegmentFocus={handleSegmentFocus}
            onSegmentBlur={handleSegmentBlur}
            focusedIndex={focusedIndex}
            ariaLabel={ariaLabel || t('circleChart.ariaLabel')}
          />
          {/* Center content */}
          <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
            <div className="text-center pointer-events-auto transition-all duration-500">
              <div className="space-y-2">
                <div className="relative">
                  <div className="text-3xl sm:text-4xl font-black bg-gradient-to-r from-slate-800 via-slate-700 to-slate-900 bg-clip-text text-transparent transition-all duration-500">
                    {total}
                  </div>
                </div>
                <div className="text-sm font-semibold text-slate-600 tracking-wide transition-all duration-500">
                  {t('circleChart.total')}
                </div>
              </div>
            </div>
          </div>
          {/* Tooltip */}
          <Tooltip {...tooltip} />
        </div>
        {/* Legend */}
        <Legend segments={segments} showPercentages={showPercentages} />
      </CardContent>
    </Card>
  );
};

CircleChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
    })
  ),
  title: PropTypes.string,
  icon: PropTypes.node,
  showPercentages: PropTypes.bool,
  animationDuration: PropTypes.number,
  strokeWidth: PropTypes.number,
  minSegmentAngle: PropTypes.number,
  dynamic: PropTypes.bool,
  colors: PropTypes.arrayOf(PropTypes.string),
  gradients: PropTypes.arrayOf(
    PropTypes.shape({
      from: PropTypes.string.isRequired,
      to: PropTypes.string.isRequired,
    })
  ),
  ariaLabel: PropTypes.string,
};

export default CircleChart;